/**
 * Role-Based UI Helpers
 * Utility functions for controlling UI visibility and behavior based on user roles
 */

import { ROLES } from './permissions.js';

/**
 * List Page Helpers
 */

/**
 * Check if role can see action buttons in list page
 * System Admin, Admin, Documentation Manager, Survey Inspector can see all buttons
 * Client and Accountant can only download PDF, CSV
 */
export const canSeeListActionButtons = (userRole) => {
  const adminRoles = [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR];
  return adminRoles.includes(userRole);
};

/**
 * Check if role is restricted in list (Client or Accountant)
 */
export const isListViewOnlyRole = (userRole) => {
  return userRole === ROLES.CLIENT || userRole === ROLES.ACCOUNTANT;
};

/**
 * Get visible action buttons for list page
 * Accountant: only view, downloadPDF, downloadCSV
 * Client: only view, downloadPDF, downloadCSV
 * Survey Inspector: view, download, edit, delete, send email (no publish/unpublish)
 * DM/Admin: all buttons including publish/unpublish
 */
export const getListActionButtons = (userRole) => {
  const publishRoles = [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER];
  const adminRoles = [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR];
  const isAccountantOrClient = userRole === ROLES.ACCOUNTANT || userRole === ROLES.CLIENT;
  
  return {
    // All roles can view and download
    view: true,
    downloadCertificate: !isAccountantOrClient,
    downloadPDF: true,
    downloadCSV: true,
    
    // Publish/Unpublish: DM and admins only (not Survey Inspector)
    publish: publishRoles.includes(userRole),
    edit: adminRoles.includes(userRole),
    delete: adminRoles.includes(userRole),
    sendEmail: adminRoles.includes(userRole),
  };
};

/**
 * Add/Edit Page Helpers
 */

/**
 * Check if survey inspector field should be locked in Add page
 * Survey Inspector: field is auto-filled with their name and locked
 * Other roles: can select any inspector
 */
export const shouldLockInspectorInAdd = (userRole) => {
  return userRole === ROLES.SURVEY_INSPECTOR;
};

/**
 * Check if survey inspector field should be locked in Edit page
 * Survey Inspector: field is locked to their name
 * Other roles: can change the inspector
 */
export const shouldLockInspectorInEdit = (userRole) => {
  return userRole === ROLES.SURVEY_INSPECTOR;
};

/**
 * Get inspector field configuration for Add page
 */
export const getAddPageInspectorConfig = (userRole, user, inspectors) => {
  const isSurveyInspector = userRole === ROLES.SURVEY_INSPECTOR;
  
  return {
    isLocked: isSurveyInspector,
    isRequired: true,
    defaultValue: isSurveyInspector ? user?.user_id : '',
    defaultLabel: isSurveyInspector ? `${user?.name} (You)` : '',
    readOnlyOptions: isSurveyInspector 
      ? inspectors.filter(i => i.user_id === user?.user_id)
      : inspectors,
  };
};

/**
 * Get inspector field configuration for Edit page
 */
export const getEditPageInspectorConfig = (userRole, user, inspectors, currentInspectorId) => {
  const isSurveyInspector = userRole === ROLES.SURVEY_INSPECTOR;
  
  return {
    isLocked: isSurveyInspector,
    isRequired: true,
    defaultValue: isSurveyInspector ? user?.user_id : currentInspectorId,
    readOnlyOptions: isSurveyInspector 
      ? inspectors.filter(i => i.user_id === user?.user_id)
      : inspectors,
    disableChanges: isSurveyInspector,
  };
};

/**
 * View Page Helpers
 */

/**
 * Check if all buttons are visible in view page
 * All roles can see all buttons, but some may be disabled based on backend permissions
 */
export const getViewPageActionButtons = (userRole) => {
  // All buttons visible, but Client and Accountant can only view/download
  return {
    // All roles
    view: true,
    downloadPDF: true,
    downloadCSV: true,
    
    // Only for admin roles
    edit: ![ROLES.CLIENT, ROLES.ACCOUNTANT].includes(userRole),
    delete: ![ROLES.CLIENT, ROLES.ACCOUNTANT].includes(userRole),
    publish: ![ROLES.CLIENT, ROLES.ACCOUNTANT].includes(userRole),
    sendEmail: ![ROLES.CLIENT, ROLES.ACCOUNTANT].includes(userRole),
  };
};

/**
 * Get disabled state for action buttons in view page
 * Client and Accountant can only view and download
 */
export const isViewPageActionDisabled = (userRole, action) => {
  if (userRole === ROLES.CLIENT || userRole === ROLES.ACCOUNTANT) {
    // Only view and download actions are allowed
    return !['view', 'downloadPDF', 'downloadCSV'].includes(action);
  }
  return false;
};

/**
 * Role Helper Functions
 */

export const isSurveyInspectorRole = (userRole) => {
  return userRole === ROLES.SURVEY_INSPECTOR;
};

export const isClientRole = (userRole) => {
  return userRole === ROLES.CLIENT;
};

export const isAccountantRole = (userRole) => {
  return userRole === ROLES.ACCOUNTANT;
};

export const isAdminRole = (userRole) => {
  const adminRoles = [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER];
  return adminRoles.includes(userRole);
};

export const isFullAccessRole = (userRole) => {
  const fullAccessRoles = [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.DOCUMENTATION_MANAGER, ROLES.SURVEY_INSPECTOR];
  return fullAccessRoles.includes(userRole);
};

/** Records with paid payment status must not be edited or deleted from list actions */
export const isPaidRecord = (paymentStatus) =>
  String(paymentStatus || '').toLowerCase() === 'paid';

const roleBasedUIHelpersExport = {
  // List page
  canSeeListActionButtons,
  isListViewOnlyRole,
  getListActionButtons,
  
  // Add/Edit page
  shouldLockInspectorInAdd,
  shouldLockInspectorInEdit,
  getAddPageInspectorConfig,
  getEditPageInspectorConfig,
  
  // View page
  getViewPageActionButtons,
  isViewPageActionDisabled,
  
  // Role helpers
  isSurveyInspectorRole,
  isClientRole,
  isAccountantRole,
  isAdminRole,
  isFullAccessRole,
  isPaidRecord,
};

export default roleBasedUIHelpersExport;
